/** @jsxImportSource @emotion/react */

import type { ProjectStateLifecycle } from '@/Client/Layout';
import { UNASSIGNED_CONTRACTOR_ID } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { formatUsd } from '@/Client/Presenters';
import { useScanEditingContextSlice } from '@/Client/State';
import { type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { MetaList } from '@/Client/Widgets/MetaList';
import { pluralize } from '@art-suite/art-core-ts';
import { getContractorOverviewTasks, getContractorOverviewTotalCost, getGroupOverviewReviewedTasks } from './focusedGroupOverviewLib';
import {
  OVERVIEW_HEADER_AMOUNT_STYLE,
  OVERVIEW_HEADER_BOX_STYLE,
  OVERVIEW_HEADER_TITLE_BLOCK_STYLE,
  OVERVIEW_HEADER_TITLE_ROW_STYLE,
  OVERVIEW_HEADER_TITLE_STYLE,
} from './focusedOverviewHeaderStyles';

/**
 * The contractor overview box — the same box the scope and a group draw, holding
 * one contractor's numbers instead. A contractor is not a real scan part (its
 * tasks can span several groups), so its tasks are resolved by contractor
 * assignment rather than read off one `group.tasks`.
 */
export const FocusedContractorOverviewHeader = ({
  contractorId,
  groups,
  projectStateLifecycle,
}: {
  contractorId: string;
  /** Every group in the scan — a contractor's tasks are gathered across all of them. */
  groups: readonly ScanGroupOptimized[];
  projectStateLifecycle: ProjectStateLifecycle;
}) => {
  const { contractorsMap, approvalStatusByTaskId, getContractorForTask } = useScanEditingContextSlice([
    'contractorsMap',
    'approvalStatusByTaskId',
    'getContractorForTask',
  ]);

  const contractorName = contractorId === UNASSIGNED_CONTRACTOR_ID
    ? 'Unassigned'
    : contractorsMap?.[contractorId]?.name?.trim() || 'Unnamed contractor';
  const tasks = getContractorOverviewTasks(groups, contractorId, { getContractorForTask });
  const reviewedCount = getGroupOverviewReviewedTasks(tasks, approvalStatusByTaskId).length;

  return (
    <div css={OVERVIEW_HEADER_BOX_STYLE} data-testid='focused-contractor-overview-header'>
      <div css={OVERVIEW_HEADER_TITLE_ROW_STYLE}>
        <div css={OVERVIEW_HEADER_TITLE_BLOCK_STYLE}>
          <h2 css={OVERVIEW_HEADER_TITLE_STYLE}>{contractorName}</h2>
          <MetaList
            parts={[
              pluralize(tasks.length, 'task'),
              ...projectStateLifecycle === 'review' ? [`${reviewedCount} reviewed`] : [],
            ]}
          />
        </div>
        <span css={OVERVIEW_HEADER_AMOUNT_STYLE}>
          {formatUsd(getContractorOverviewTotalCost(tasks), false, true)}
        </span>
      </div>
    </div>
  );
};
